import ExpandMore from "@mui/icons-material/ChevronRight";
import OpenInNew from "@mui/icons-material/OpenInNew";
import {
  Box,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  useTheme,
  type Theme,
} from "@mui/material";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { kebabCase } from "lodash-es";
import {
  type ElementType,
  type MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { NavLink, useLocation } from "react-router-dom";

import { useLocalStorageState } from "../../hooks/useLocalStorageState";
import { useStaffPreviewingFlag } from "../../hooks/useStaffPreviewingFlag";
import { useAlerts } from "../Alerts/AlertProvider";
import {
  PREVIEW_ICON_FONT_SIZE,
  PREVIEW_ICON_GAP,
  type PreviewVariant,
} from "../PreviewFeature/constants";
import { PreviewFeatureIndicator } from "../PreviewFeature/PreviewFeatureIndicator";
import {
  NAV_COLLAPSED_BUTTON_SIZE,
  NAV_COLLAPSED_ROW_PADDING_Y,
  NAV_ROW_CONTENT_HEIGHT,
  NAV_ROW_GUTTER,
} from "./navMetrics";

/**
 * Which audience a nav item belongs to. Drives the resting icon color via the
 * theme `nav` palette — customer items use the muted-teal `icon` token, staff
 * controls use the pumpkin `staffIcon` token. Defaults to "customer".
 */
export type NavVariant = "customer" | "staff";

type BaseLinkNavItem = {
  text: string;
  description?: string;
  path: string;
  isDisabled?: boolean;
  variant?: NavVariant;
  textColor?: string;
  isExternal?: boolean;
  matchPaths?: string[];
  /** Large brand image used for the home-page tile only; the left nav keeps `Icon`. */
  tileImage?: string;
  /** Dark-mode variant of `tileImage`; falls back to `tileImage` when unset. */
  tileImageDark?: string;
  /** When set, shows preview affordance if staff is previewing this in-development flag. */
  previewFlag?: FeatureFlagKey;
  /** Stable DOM id for one-off product tours (e.g. Scout launch spotlight). */
  tourAnchorId?: string;
  /** `newVersion` when an existing experience stays available behind the preview. Defaults to `gated`. */
  previewVariant?: PreviewVariant;
  /** Fired on click (navigation still proceeds); used for analytics side-effects. */
  onSelect?: () => void;
};

export type StandardNavItem = BaseLinkNavItem & {
  kind: "Standard";
  Icon: ElementType;
};

export type GroupChildNavItem = BaseLinkNavItem & {
  kind: "GroupChild";
};

export type GroupNavItem = {
  kind: "Group";
  text: string;
  description?: string;
  Icon: ElementType;
  path: string;
  isExpandable?: boolean;
  variant?: NavVariant;
  defaultExpanded?: boolean;
  children: GroupChildNavItem[];
  /** Large brand image used for the home-page tile only; the left nav keeps `Icon`. */
  tileImage?: string;
  /** Dark-mode variant of `tileImage`; falls back to `tileImage` when unset. */
  tileImageDark?: string;
};

type NavItemProps = {
  navItem: StandardNavItem | GroupNavItem | GroupChildNavItem;
  collapsed: boolean;
  onNavigate?: () => void;
};

const NAV_ITEM_BORDER_RADIUS = "8px";

function getInternalUrl(path: string | undefined, text: string) {
  const resolvedPath = path ?? text.split("/").map(kebabCase).join("/");
  return `/${resolvedPath}`;
}

function getMatchPaths(path: string | undefined, text: string, matchPaths: string[] = []) {
  return [
    getInternalUrl(path, text),
    ...matchPaths.map((matchPath) => getInternalUrl(matchPath, text)),
  ];
}

function isPathMatch(currentPath: string, candidatePath: string) {
  return currentPath === candidatePath || currentPath.startsWith(`${candidatePath}/`);
}

function getExpandGroupNavRequest(state: unknown): string | undefined {
  if (state && typeof state === "object" && "expandGroupNav" in state) {
    const value = state.expandGroupNav;
    return typeof value === "string" ? value : undefined;
  }
  return undefined;
}

function isNavItemActive(
  currentPath: string,
  item: Pick<BaseLinkNavItem, "path" | "text" | "matchPaths" | "isExternal">
) {
  if (item.isExternal) {
    return false;
  }

  return getMatchPaths(item.path, item.text, item.matchPaths).some((candidatePath) =>
    isPathMatch(currentPath, candidatePath)
  );
}

function navIconColor(theme: Theme, variant: NavVariant, isActive: boolean) {
  // Resting icon color comes from the theme `nav` palette, keyed on the item's
  // variant: customer → muted-teal `icon`, staff → pumpkin `staffIcon`. The
  // active item sits on a cyan fill, so its icon is white for contrast.
  if (isActive) {
    return theme.palette.nav.selectedFg;
  }

  return variant === "staff" ? theme.palette.nav.staffIcon : theme.palette.nav.icon;
}

// The vertical connector line beside a group child. Staff keeps the pumpkin
// accent; customer items use the subtle teal divider so the brand "pop" color
// isn't spent on every connector.
function navConnectorColor(theme: Theme, variant: NavVariant | undefined) {
  return variant === "staff" ? theme.palette.nav.staffIcon : theme.palette.nav.divider;
}

/**
 * A nav icon whose resting color is owned by the theme. The nav row declares
 * what the item is (`variant`) and whether it's `active`; the color values live
 * in `theme.palette.nav`, so no component threads colors into this component.
 */
function NavIcon({
  icon: Icon,
  variant = "customer",
  active,
  fontSize,
}: {
  icon: ElementType;
  variant?: NavVariant;
  active: boolean;
  fontSize?: string;
}) {
  return (
    <Icon
      sx={(theme: Theme) => ({
        ...(fontSize ? { fontSize } : {}),
        color: navIconColor(theme, variant, active),
      })}
    />
  );
}

export function getNavItemUrl(navItem: Pick<BaseLinkNavItem, "path" | "text" | "isExternal">) {
  if (navItem.isExternal) {
    return navItem.path ?? "";
  }

  return getInternalUrl(navItem.path, navItem.text);
}

function getNavItemTooltipTitle(navItem: Pick<BaseLinkNavItem, "text" | "isExternal">) {
  return navItem.isExternal ? `${navItem.text} (opens in new tab)` : navItem.text;
}

function NavPreviewSuffix({
  previewFlag,
  previewVariant,
  isActive,
  textColor,
}: {
  previewFlag?: FeatureFlagKey;
  previewVariant?: PreviewVariant;
  isActive: boolean;
  textColor?: string;
}) {
  const theme = useTheme();
  const isPreview = useStaffPreviewingFlag(previewFlag);

  if (!isPreview) {
    return null;
  }

  const iconColor = textColor ?? (isActive ? theme.palette.nav.selectedFg : theme.palette.nav.icon);

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        flexShrink: 0,
        verticalAlign: "middle",
      }}
    >
      <PreviewFeatureIndicator
        fontSize={PREVIEW_ICON_FONT_SIZE}
        inline
        color={iconColor}
        variant={previewVariant}
      />
    </Box>
  );
}

function NavItemPrimaryLabel({
  text,
  previewFlag,
  previewVariant,
  isActive,
  textColor,
}: {
  text: string;
  previewFlag?: FeatureFlagKey;
  previewVariant?: PreviewVariant;
  isActive: boolean;
  textColor?: string;
}) {
  return (
    <>
      <Box
        component="span"
        sx={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          minWidth: 0,
        }}
      >
        {text}
      </Box>
      <NavPreviewSuffix
        previewFlag={previewFlag}
        previewVariant={previewVariant}
        isActive={isActive}
        textColor={textColor}
      />
    </>
  );
}

function navItemPrimaryTypographySx(isActive: boolean, textColor?: string) {
  return (theme: Theme) => ({
    color: textColor ?? (isActive ? theme.palette.nav.selectedFg : "white"),
    display: "inline-flex",
    alignItems: "center",
    flexWrap: "nowrap",
    gap: PREVIEW_ICON_GAP,
    maxWidth: "100%",
    minWidth: 0,
  });
}

function getGroupNavItemUrl(navItem: GroupNavItem) {
  if (!navItem.isExpandable) {
    return getInternalUrl(navItem.path, navItem.text);
  }

  const firstChild = navItem.children[0];
  return firstChild ? getNavItemUrl(firstChild) : getInternalUrl(navItem.path, navItem.text);
}

function handleDisabledNavItemClick(
  event: MouseEvent<HTMLAnchorElement>,
  navItem: Pick<BaseLinkNavItem, "isDisabled">,
  alerts: ReturnType<typeof useAlerts>
) {
  if (navItem.isDisabled) {
    event.preventDefault();
    alerts.info("This section is not yet available.");
  }
}

function StandardNavLink({
  navItem,
  collapsed,
  onNavigate,
}: {
  navItem: StandardNavItem;
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  const alerts = useAlerts();
  const location = useLocation();
  const isActive = isNavItemActive(location.pathname, navItem);
  const url = getNavItemUrl(navItem);
  const tooltipTitle = getNavItemTooltipTitle(navItem);

  if (collapsed) {
    return (
      <ListItem
        disablePadding
        sx={{
          display: "flex",
          justifyContent: "center",
          px: 1.5,
          // Pads the collapsed button up to the shared row pitch, so rows
          // don't drift vertically when the nav collapses.
          py: `${NAV_COLLAPSED_ROW_PADDING_Y}px`,
        }}
      >
        <Tooltip
          title={tooltipTitle}
          placement="right"
          enterDelay={100}
          disableInteractive
          slotProps={{
            tooltip: {
              sx: {
                pointerEvents: "none",
              },
            },
          }}
        >
          <NavLink
            aria-label={tooltipTitle}
            onClick={(event) => {
              handleDisabledNavItemClick(event, navItem, alerts);
              if (!navItem.isDisabled) {
                navItem.onSelect?.();
                onNavigate?.();
              }
            }}
            to={url}
            target={navItem.isExternal ? "_blank" : undefined}
            rel={navItem.isExternal ? "noopener noreferrer" : undefined}
            style={{
              textDecoration: "none",
              color: "inherit",
              display: "block",
              width: "100%",
            }}
          >
            <ListItemButton
              id={navItem.tourAnchorId}
              sx={(theme) => ({
                minHeight: NAV_COLLAPSED_BUTTON_SIZE,
                minWidth: NAV_COLLAPSED_BUTTON_SIZE,
                justifyContent: "center",
                px: 0,
                py: 1,
                borderRadius: 999,
                backgroundColor: isActive ? theme.palette.nav.selectedBg : "transparent",
                "&:hover": {
                  backgroundColor: isActive ? theme.palette.nav.selectedBg : "transparent",
                  ".MuiSvgIcon-root": {
                    color: isActive ? theme.palette.nav.selectedFg : theme.palette.nav.selected,
                  },
                },
              })}
            >
              <NavIcon
                icon={navItem.Icon}
                variant={navItem.variant}
                active={isActive}
                fontSize="24px"
              />
            </ListItemButton>
          </NavLink>
        </Tooltip>
      </ListItem>
    );
  }

  return (
    <ListItem disablePadding>
      <NavLink
        aria-label={navItem.text}
        onClick={(event) => {
          handleDisabledNavItemClick(event, navItem, alerts);
          if (!navItem.isDisabled) {
            navItem.onSelect?.();
            onNavigate?.();
          }
        }}
        to={url}
        target={navItem.isExternal ? "_blank" : undefined}
        rel={navItem.isExternal ? "noopener noreferrer" : undefined}
        style={{
          textDecoration: "none",
          color: "inherit",
          width: "100%",
          marginLeft: "8px",
          marginRight: "8px",
          display: "flex",
          justifyContent: "center",
          flexDirection: "row",
        }}
      >
        <ListItemButton
          id={navItem.tourAnchorId}
          sx={(theme) => ({
            my: `${NAV_ROW_GUTTER}px`,
            paddingY: 0,
            // Pill + its margins add up to the shared row pitch.
            minHeight: `${NAV_ROW_CONTENT_HEIGHT}px`,
            background: isActive ? theme.palette.nav.selectedBg : "none",
            pl: "16px",
            pr: 2,
            borderRadius: NAV_ITEM_BORDER_RADIUS,
            "&:hover": {
              backgroundColor: isActive ? theme.palette.nav.selectedBg : "transparent",
              ".MuiSvgIcon-root": {
                color: isActive ? theme.palette.nav.selectedFg : theme.palette.nav.selected,
              },
              ".MuiTypography-root": {
                color: isActive ? theme.palette.nav.selectedFg : theme.palette.nav.selected,
              },
            },
          })}
        >
          <ListItemIcon
            sx={{
              minWidth: 24,
              mr: "12px",
            }}
          >
            <NavIcon
              icon={navItem.Icon}
              variant={navItem.variant}
              active={isActive}
              fontSize="24px"
            />
          </ListItemIcon>
          <ListItemText
            sx={{ minWidth: 0, flex: 1 }}
            primary={
              <NavItemPrimaryLabel
                text={navItem.text}
                previewFlag={navItem.previewFlag}
                previewVariant={navItem.previewVariant}
                isActive={isActive}
                textColor={navItem.textColor}
              />
            }
            slotProps={{
              primary: { sx: navItemPrimaryTypographySx(isActive, navItem.textColor) },
            }}
          />
          {navItem.isExternal && (
            <NavIcon icon={OpenInNew} variant={navItem.variant} active={isActive} />
          )}
        </ListItemButton>
      </NavLink>
    </ListItem>
  );
}

function GroupChildNavLink({
  navItem,
  onNavigate,
}: {
  navItem: GroupChildNavItem;
  onNavigate?: () => void;
}) {
  const alerts = useAlerts();
  const location = useLocation();
  const isActive = isNavItemActive(location.pathname, navItem);
  const url = getNavItemUrl(navItem);

  const selectedHeight = `${NAV_ROW_CONTENT_HEIGHT}px`;

  return (
    <ListItem
      disablePadding
      sx={(theme) => ({
        display: "flex",
        height: "40px",
        px: "8px",
        "&:hover": {
          ".MuiTypography-root": {
            color: isActive ? theme.palette.nav.selectedFg : theme.palette.nav.selected,
          },
        },
      })}
    >
      <NavLink
        aria-label={navItem.text}
        id={navItem.tourAnchorId}
        onClick={(event) => {
          handleDisabledNavItemClick(event, navItem, alerts);
          if (!navItem.isDisabled) {
            onNavigate?.();
          }
        }}
        to={url}
        target={navItem.isExternal ? "_blank" : undefined}
        rel={navItem.isExternal ? "noopener noreferrer" : undefined}
        style={{
          textDecoration: "none",
          color: "inherit",
          width: "100%",
          display: "contents",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "28px",
            height: "100%",
            p: 0,
            boxSizing: "border-box",
          }}
        >
          <Box
            sx={(theme) => ({
              width: "100%",
              height: selectedHeight,

              borderTopLeftRadius: NAV_ITEM_BORDER_RADIUS,
              borderBottomLeftRadius: NAV_ITEM_BORDER_RADIUS,
              background: isActive ? theme.palette.nav.selectedBg : "none",
            })}
          />
        </Box>
        <Box
          aria-hidden
          sx={{
            width: "1px",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            flexShrink: 0,
          }}
        >
          <Box
            sx={(theme) => ({
              width: "100%",
              flex: 1,
              backgroundColor: navConnectorColor(theme, navItem.variant),
            })}
          />
          <Box
            sx={(theme) => ({
              width: "100%",
              height: selectedHeight,
              flexShrink: 0,
              backgroundColor: isActive
                ? theme.palette.nav.selectedFg
                : navConnectorColor(theme, navItem.variant),
            })}
          />
          <Box
            sx={(theme) => ({
              width: "100%",
              flex: 1,
              backgroundColor: navConnectorColor(theme, navItem.variant),
            })}
          />
        </Box>
        <Box
          sx={(theme) => ({
            display: "flex",
            flex: 1,
            minWidth: 0,

            justifyContent: "center",
            flexDirection: "row",
            height: selectedHeight,
            borderTopRightRadius: NAV_ITEM_BORDER_RADIUS,
            borderBottomRightRadius: NAV_ITEM_BORDER_RADIUS,
            background: isActive ? theme.palette.nav.selectedBg : "none",
          })}
        >
          <ListItemButton
            sx={{
              my: 0,
              py: 0,
              pl: 3,
              pr: 0,
              borderTopRightRadius: NAV_ITEM_BORDER_RADIUS,
              borderBottomRightRadius: NAV_ITEM_BORDER_RADIUS,
            }}
          >
            <ListItemText
              primary={
                <NavItemPrimaryLabel
                  text={navItem.text}
                  previewFlag={navItem.previewFlag}
                  previewVariant={navItem.previewVariant}
                  isActive={isActive}
                  textColor={navItem.textColor}
                />
              }
              slotProps={{
                primary: {
                  sx: (theme) => ({
                    fontSize: "14px",
                    lineHeight: "24px",
                    marginLeft: 0,
                    ...navItemPrimaryTypographySx(isActive, navItem.textColor)(theme),
                  }),
                },
              }}
            />
          </ListItemButton>
        </Box>
      </NavLink>
    </ListItem>
  );
}

function GroupNavRow({
  navItem,
  collapsed,
  onNavigate,
}: {
  navItem: GroupNavItem;
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  const location = useLocation();
  const groupUrl = getGroupNavItemUrl(navItem);
  const collapseId = `nav-group-${kebabCase(navItem.text)}`;
  const storageKey = `nav-menu-${navItem.text}-group-expanded`;
  const isExpandable = navItem.isExpandable ?? true;

  const groupIsActive = useMemo(() => {
    if (!isExpandable) {
      return isNavItemActive(location.pathname, {
        path: navItem.path,
        text: navItem.text,
      });
    }

    return navItem.children.some((child) => isNavItemActive(location.pathname, child));
  }, [isExpandable, location.pathname, navItem.children, navItem.path, navItem.text]);
  const [persistedExpanded, setPersistedExpanded] = useLocalStorageState<boolean>(storageKey, {
    defaultValue: navItem.defaultExpanded ?? false,
  });
  const [expanded, setExpanded] = useState(persistedExpanded);
  const groupShowsActiveState = useMemo(() => {
    if (!isExpandable) {
      return groupIsActive;
    }

    if (expanded && !collapsed) {
      return false;
    }

    return groupIsActive;
  }, [collapsed, expanded, groupIsActive, isExpandable]);

  const updateExpanded = useCallback(
    (nextExpanded: boolean | ((currentExpanded: boolean) => boolean)) => {
      const resolvedExpanded =
        typeof nextExpanded === "function" ? nextExpanded(expanded) : nextExpanded;

      setExpanded(resolvedExpanded);
      setPersistedExpanded(resolvedExpanded);
    },
    [expanded, setPersistedExpanded]
  );

  const expandGroupNavRequest = getExpandGroupNavRequest(location.state);
  // Respond to a navigation event from the landing page tile (a sibling
  // component) that asks this group to unfurl. Localstorage persistence rules
  // out a setState-during-render approach.
  useEffect(() => {
    if (isExpandable && expandGroupNavRequest === navItem.text) {
      // oxlint-disable-next-line react-you-might-not-need-an-effect/no-derived-state, react-you-might-not-need-an-effect/no-pass-data-to-parent
      setExpanded(true);
      setPersistedExpanded(true);
    }
  }, [expandGroupNavRequest, isExpandable, navItem.text, setPersistedExpanded]);

  if (collapsed) {
    const tooltipTitle = navItem.text;

    return (
      <ListItem
        disablePadding
        sx={{
          display: "flex",
          justifyContent: "center",
          px: 1.5,
          // Pads the collapsed button up to the shared row pitch.
          py: `${NAV_COLLAPSED_ROW_PADDING_Y}px`,
        }}
      >
        <Tooltip
          title={tooltipTitle}
          placement="right"
          enterDelay={100}
          disableInteractive
          slotProps={{
            tooltip: {
              sx: {
                pointerEvents: "none",
              },
            },
          }}
        >
          <NavLink
            aria-label={tooltipTitle}
            onClick={() => {
              onNavigate?.();
            }}
            to={groupUrl}
            style={{
              textDecoration: "none",
              color: "inherit",
              display: "block",
              width: "100%",
            }}
          >
            <ListItemButton
              selected={groupShowsActiveState}
              sx={(theme) => ({
                minHeight: NAV_COLLAPSED_BUTTON_SIZE,
                minWidth: NAV_COLLAPSED_BUTTON_SIZE,
                justifyContent: "center",
                px: 0,
                py: 1,
                borderRadius: 999,
                "&.Mui-selected": {
                  backgroundColor: theme.palette.nav.selectedBg,
                },
                "&.Mui-selected:hover": {
                  backgroundColor: theme.palette.nav.selectedBg,
                },
                "&:hover": {
                  backgroundColor: groupShowsActiveState ? theme.palette.nav.selectedBg : "none",
                  ".MuiSvgIcon-root": {
                    color: groupShowsActiveState
                      ? theme.palette.nav.selectedFg
                      : theme.palette.nav.selected,
                  },
                },
              })}
            >
              <NavIcon
                icon={navItem.Icon}
                variant={navItem.variant}
                active={groupShowsActiveState}
                fontSize="24px"
              />
            </ListItemButton>
          </NavLink>
        </Tooltip>
      </ListItem>
    );
  }

  // The ListItem is a flex column (not block) so the row's 8px margins are
  // contained instead of collapsing between rows — keeping the expanded pitch
  // at a true 48px, matching the collapsed icon rail.
  return (
    <ListItem
      disablePadding
      sx={{ display: "flex", flexDirection: "column", alignItems: "stretch" }}
    >
      <Box
        sx={(theme) => ({
          p: 0,
          m: `${NAV_ROW_GUTTER}px`,
          // Row content + its margins add up to the shared row pitch.
          height: `${NAV_ROW_CONTENT_HEIGHT}px`,
          display: "flex",
          alignItems: "center",
          "&:hover": {
            backgroundColor: groupShowsActiveState ? theme.palette.nav.selectedBg : "none",
            ".MuiSvgIcon-root": {
              color: groupShowsActiveState
                ? theme.palette.nav.selectedFg
                : theme.palette.nav.selected,
            },
            ".MuiTypography-root": {
              color: groupShowsActiveState
                ? theme.palette.nav.selectedFg
                : theme.palette.nav.selected,
            },
          },
          background: groupShowsActiveState ? theme.palette.nav.selectedBg : "none",
          borderRadius: NAV_ITEM_BORDER_RADIUS,
        })}
      >
        <NavLink
          aria-label={navItem.text}
          onClick={() => {
            if (isExpandable) {
              updateExpanded(true);
            }
            onNavigate?.();
          }}
          to={groupUrl}
          style={{
            textDecoration: "none",
            color: "inherit",
            width: "100%",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          <ListItemIcon
            sx={{
              pl: "16px",
              minWidth: 24,
              mr: "12px",
            }}
          >
            <NavIcon icon={navItem.Icon} variant={navItem.variant} active={groupShowsActiveState} />
          </ListItemIcon>

          <ListItemButton
            sx={{
              p: 0,
              minHeight: `${NAV_ROW_CONTENT_HEIGHT}px`,
              minWidth: 0,
              borderRadius: NAV_ITEM_BORDER_RADIUS,
              backgroundColor: "transparent",
              "&:hover": {
                backgroundColor: "transparent",
              },
            }}
          >
            <ListItemText
              primary={navItem.text}
              slotProps={{
                primary: {
                  sx: (theme) => ({
                    fontSize: "16px",
                    color: groupShowsActiveState ? theme.palette.nav.selectedFg : "white",
                    ml: 0,
                  }),
                },
              }}
            />
          </ListItemButton>

          {isExpandable && (
            <IconButton
              aria-controls={collapseId}
              aria-expanded={expanded}
              aria-label={`${expanded ? "Collapse" : "Expand"} ${navItem.text}`}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                updateExpanded((current) => !current);
              }}
              size="small"
              sx={{
                mr: "4px",
                borderRadius: NAV_ITEM_BORDER_RADIUS,
              }}
            >
              <ExpandMore
                sx={{
                  color: "white",
                  transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
                  transition: "transform 0.2s ease-in-out",
                }}
              />
            </IconButton>
          )}
        </NavLink>
      </Box>
      {isExpandable && (
        <Collapse id={collapseId} in={expanded} timeout="auto" unmountOnExit>
          <List disablePadding>
            {navItem.children.map((child) => (
              <GroupChildNavLink
                key={`${navItem.text}-${child.text}`}
                navItem={child}
                onNavigate={onNavigate}
              />
            ))}
          </List>
        </Collapse>
      )}
    </ListItem>
  );
}

export function NavItemV2({ navItem, collapsed, onNavigate }: NavItemProps) {
  if (navItem.kind === "Group") {
    return <GroupNavRow navItem={navItem} collapsed={collapsed} onNavigate={onNavigate} />;
  }

  if (navItem.kind === "Standard") {
    return <StandardNavLink navItem={navItem} collapsed={collapsed} onNavigate={onNavigate} />;
  }

  return <GroupChildNavLink navItem={navItem} onNavigate={onNavigate} />;
}
